import { Badge } from "@/components/ui/badge";
import { endpoint } from "@services/api.services";
import { METHODS } from "@services/interface.service";
import { formatDateTime, showToast } from "@utils/helper/index.helper";
import { useHandleAction } from "@utils/hook/index.hook";
import { ColumnType } from "@utils/interface/index.interface";
import { CertificateWebhookConfigData } from "@utils/interface/journey.interface";
import { usePostData } from "@utils/query/index.query";
import { useSelectionStore } from "@utils/states/index.state";
import { useMemo } from "react";

interface ColumnListProps {
  refetch?: () => void;
}

/* the url carries the space key and token — show enough to identify the space
   and nothing an onlooker could post with */
const maskWebhookUrl = (url?: string) => {
  if (!url) return "-";
  try {
    const parsed = new URL(url);
    const space = parsed.pathname.split("/").filter(Boolean)[2] ?? "unknown";
    return `spaces/${space} · key=••••  token=••••`;
  } catch {
    return "Invalid URL";
  }
};

export const ColumnList = ({ refetch }: ColumnListProps) => {
  const { handleAction } = useHandleAction({
    endpoint: endpoint["certificate-webhook-config"],
    refetch,
  });
  const { clearSelection } = useSelectionStore();
  const { mutate, isPending } = usePostData();

  const handleActivate = (id: number) => {
    mutate(
      {
        endpoint: endpoint["certificate-webhook-config"],
        method: METHODS.PATCH,
        data: { id, is_active: true },
      },
      {
        onSuccess: () => {
          refetch && refetch();
          showToast({
            title: "Webhook",
            description: "This webhook is now active",
            type: "success",
          });
        },
        onError: () =>
          showToast({
            title: "Webhook",
            description: "Could not activate this webhook",
            type: "error",
          }),
      }
    );
  };

  const columns: ColumnType<CertificateWebhookConfigData>[] = useMemo(
    () => [
      {
        name: "Name",
        cell: (row) => (
          <span className="font-medium text-neutral-crm-900">
            {row?.name || "-"}
          </span>
        ),
        sortable: true,
      },
      {
        name: "Webhook",
        cell: (row) => (
          <span className="block max-w-[280px] break-words text-[13px] text-gray-500">
            {maskWebhookUrl(row?.webhook_url)}
          </span>
        ),
        sortable: false,
      },
      {
        name: "Status",
        cell: (row) =>
          row?.is_active ? (
            <Badge className="bg-[#1DA077] hover:bg-[#1DA077]">Active</Badge>
          ) : (
            <Badge variant="outline">Inactive</Badge>
          ),
        sortable: true,
      },
      {
        name: "Last updated",
        cell: (row) => (
          <span className="text-[13px] text-gray-500">
            {formatDateTime(row?.updated_at) || "-"}
          </span>
        ),
        sortable: true,
      },
      {
        name: "Actions",
        cell: (row) => (
          <div className="flex items-center gap-3 cursor-pointer font-[500] select-none">
            {!row?.is_active && (
              <button
                type="button"
                disabled={isPending}
                className="text-[#1DA077] cursor-pointer disabled:opacity-60"
                onClick={() => handleActivate(row.id)}
                title="Make this the active webhook"
              >
                Activate
              </button>
            )}
            <button
              type="button"
              className="text-[#1DA077] cursor-pointer"
              onClick={() => handleAction({ type: "modal-edit", data: row.id })}
              title="Edit"
            >
              Edit
            </button>
            <button
              type="button"
              className="text-[#C33241] cursor-pointer"
              onClick={() => {
                clearSelection();
                handleAction({
                  type: "confirm",
                  data: { type: "delete", id: row.id },
                });
              }}
              title="Delete"
            >
              Delete
            </button>
          </div>
        ),
        sortable: false,
      },
    ],
    [handleAction, clearSelection, isPending]
  );

  return columns;
};
